import type { CityConfig } from "../hawaii-city/types";

export const hiloConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Hilo",
  state: "Hawaii",
  stateAbbr: "HI",
  slug: "hilo",
  route: "/cities/hilo",
  title: "Emergency & Expedited Passport Services in Hilo, HI — Same-Day Processing | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Hilo, HI — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Big Island and East Hawaii travelers.",
  h1: "Emergency & Expedited Passport Services in Hilo, HI",
  heroSubtitle: "Passport expired? Flying soon? We help Hilo and Big Island travelers get their passports fast. As fast as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Stunning%20view%20of%20Hilo%20Bay%20Hawaii%20with%20tropical%20rainforest%20coastline%2C%20lush%20green%20mountains%20of%20Mauna%20Kea%20in%20background%2C%20calm%20turquoise%20Pacific%20waters%2C%20dramatic%20Hawaiian%20clouds%2C%20warm%20afternoon%20sunlight%2C%20professional%20landscape%20photography%2C%20vibrant%20tropical%20colors%2C%20no%20readable%20text%20or%20signs%2C%20serene%20Big%20Island%20atmosphere&width=1800&height=700&seq=hilo-hero-01&orientation=landscape",
  heroImageAlt: "Hilo Bay and Big Island tropical coastline with Mauna Kea in background",
  geoPosition: "19.7297;-155.0900",
  geoPlacename: "Hilo, Hawaii",
  regionalAgency: {
    name: "Honolulu Passport Agency",
    address: "300 Ala Moana Blvd, Room 1-224, Honolulu, HI 96850",
    distance: "Inter-island flight to Honolulu — about 200 miles",
  },
  nearbyCitiesZips: [
    { name: "Hilo", zips: "96720" },
    { name: "Pahoa", zips: "96778" },
    { name: "Keaau", zips: "96749" },
    { name: "Mountain View", zips: "96771" },
    { name: "Kurtistown", zips: "96760" },
    { name: "Honokaa", zips: "96727" },
    { name: "Waimea", zips: "96743" },
    { name: "Kailua-Kona", zips: "96740" },
    { name: "Kealakekua", zips: "96750" },
    { name: "Captain Cook", zips: "96704" },
    { name: "Volcano", zips: "96785" },
    { name: "Hawi", zips: "96719" },
    { name: "Kapaau", zips: "96755" },
    { name: "Laupahoehoe", zips: "96764" },
    { name: "Pepeekeo", zips: "96783" },
    { name: "Papaikou", zips: "96781" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from home or work in minutes." },
    { icon: "ri-truck-line", title: "Ship Your Documents", desc: "Use overnight shipping to send your documents directly to us — we handle the rest." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission to reduce errors and delays." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed." },
  ],
  testimonials: [
    {
      name: "Keoni H.",
      location: "Hilo, HI",
      rating: 5,
      text: "I had an emergency trip to Japan from Hilo and my passport was expired. Fast Passport Center processed everything in 5 days. The fact that they could handle this from the Big Island made all the difference.",
      service: "Passport Renewal",
    },
    {
      name: "Sarah L.",
      location: "Pahoa, HI",
      rating: 5,
      text: "Our daughter needed a passport for a school trip to Australia. Living in Pahoa, we thought we'd have to fly to Honolulu. Fast Passport Center made it possible entirely online — shipped from Hilo and had it in 6 days.",
      service: "Child Passport",
    },
    {
      name: "Michael R.",
      location: "Kailua-Kona, HI",
      rating: 5,
      text: "Business trip to Seoul with only 8 days notice. Started my order from Kona, shipped overnight from Hilo, and had my passport in 4 days. Incredible service for Big Island residents.",
      service: "Expedited Passport",
    },
  ],
  facilities: [
    {
      id: "hilo-usps",
      name: "Hilo Main Post Office",
      type: "USPS Passport Acceptance",
      address: "154 Waianuenue Ave",
      city: "Hilo",
      state: "HI",
      zip: "96720",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Downtown Hilo",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary post office for Hilo and East Hawaii. Appointment required. Confirm photo services.",
      icon: "ri-mail-line",
    },
    {
      id: "hilo-keaukaha",
      name: "Keaukaha Post Office",
      type: "USPS Passport Acceptance",
      address: "190 Kalanianaole Ave",
      city: "Hilo",
      state: "HI",
      zip: "96720",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Keaukaha, Hilo",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "East Hilo option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "hilo-pahoa",
      name: "Pahoa Post Office",
      type: "USPS Passport Acceptance",
      address: "15-2888 Pahoa Village Rd",
      city: "Pahoa",
      state: "HI",
      zip: "96778",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Pahoa — about 30 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Puna District option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "hilo-keaau",
      name: "Keaau Post Office",
      type: "USPS Passport Acceptance",
      address: "16-701 Macadamia Rd",
      city: "Keaau",
      state: "HI",
      zip: "96749",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Keaau — about 10 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Keaau area option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "hilo-honokaa",
      name: "Honokaa Post Office",
      type: "USPS Passport Acceptance",
      address: "45-3400 Mamane St",
      city: "Honokaa",
      state: "HI",
      zip: "96727",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Honokaa — about 45 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Hamakua Coast option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "hilo-kona",
      name: "Kailua-Kona Post Office",
      type: "USPS Passport Acceptance",
      address: "74-5577 Palani Rd",
      city: "Kailua-Kona",
      state: "HI",
      zip: "96740",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Kailua-Kona — about 80 miles west",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "West Hawaii option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in Hilo, HI?",
      a: "Through Fast Passport Center, expedited processing can be as fast as 24 hours depending on your eligibility and the processing option you select. Standard government processing through a post office in Hilo can take 2–6 months.",
    },
    {
      q: "Where is the closest passport agency to Hilo?",
      a: "The nearest regional passport agency is in Honolulu, on the neighboring island of Oahu — about 200 miles away by air. Inter-island travel would be required. Fast Passport Center eliminates the need for you to travel to Oahu — we handle everything remotely via courier.",
    },
    {
      q: "Do I need to fly to Honolulu to get an expedited passport?",
      a: "No. With Fast Passport Center, you don't need to fly to Honolulu. Start your order online from Hilo, ship your documents overnight from the Big Island, and we handle the expedited processing through our reserved agency access.",
    },
    {
      q: "Do I need to visit an office to use Fast Passport Center?",
      a: "No — most Hilo travelers start online and ship their documents via overnight courier. There is no local Fast Passport Center office in Hilo, but that's the point: you don't need one. Start your order online, follow the checklist, and ship your documents overnight.",
    },
    {
      q: "What documents do I need for an expedited passport?",
      a: "Most applicants need: proof of U.S. citizenship (current or expired U.S. passport, birth certificate, or naturalization certificate), a government-issued photo ID, completed passport forms, and passport photos. We provide a personalized checklist after you start your order.",
    },
    {
      q: "Can I get an expedited passport if I've never had one before?",
      a: "Yes, but first-time applicants typically require an in-person acceptance clerk visit before documents can be submitted for expedited processing. We guide you through exactly what to do and where to go in the Hilo area.",
    },
    {
      q: "How long does a passport take through the post office in Hilo?",
      a: "Standard government processing through a Hilo acceptance facility can take 2–6 months depending on government workload. If your travel date is sooner than that, using an approved passport courier like Fast Passport Center is the safer option.",
    },
    {
      q: "Is Fast Passport Center an official government agency?",
      a: "No — Fast Passport Center is a private, approved and registered U.S. Department of State passport courier service. We are not a government agency. We work within the official passport system to provide expedited processing.",
    },
    {
      q: "What if I need a child passport in Hilo?",
      a: "Child passports (for minors 15 and under) require parental participation and an in-person acceptance step — both parents typically must consent. We provide clear instructions and a checklist. Expedited processing options are available after the acceptance step.",
    },
    {
      q: "Can I get a passport if mine is lost or stolen?",
      a: "Yes. Lost or stolen passport replacements often require additional forms (including Form DS-64). We guide you through exactly what's needed and help ensure the correct process is followed to avoid delays.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Hilo", address: "555 Kilauea Ave, Hilo, HI 96720", hours: "Daily 7 AM – 11 PM", website: "https://www.walgreens.com/" },
    { name: "Walmart — Hilo", address: "325 Makaala St, Hilo, HI 96720", hours: "Call for hours", website: "https://www.walmart.com/" },
    { name: "CVS Pharmacy — Hilo", address: "555 Kilauea Ave, Hilo, HI 96720", hours: "Daily 7 AM – 10 PM", website: "https://www.cvs.com/" },
    { name: "The UPS Store — Hilo", address: "315 E Makaala St, Hilo, HI 96720", hours: "Call for hours", website: "https://www.theupsstore.com/" },
    { name: "FedEx Office — Hilo", address: "500 Kalanianaole Ave, Hilo, HI 96720", hours: "Call for hours", website: "https://www.fedex.com/" },
    { name: "Walgreens — Kailua-Kona", address: "75-1029 Henry St, Kailua-Kona, HI 96740", hours: "Daily 7 AM – 11 PM", website: "https://www.walgreens.com/" },
    { name: "CVS Pharmacy — Kailua-Kona", address: "75-5595 Palani Rd, Kailua-Kona, HI 96740", hours: "Daily 7 AM – 10 PM", website: "https://www.cvs.com/" },
    { name: "Walmart — Kailua-Kona", address: "75-1015 Henry St, Kailua-Kona, HI 96740", hours: "Call for hours", website: "https://www.walmart.com/" },
  ],
  dmvLocations: [
    {
      name: "Hilo DMV — Hawaii County Vehicle Registration",
      address: "101 Pauahi St, Hilo, HI 96720",
      hours: "Mon–Fri, 7:45 AM – 4:00 PM",
      note: "Driver licenses, REAL ID, state IDs. Appointment recommended.",
      featured: true,
    },
    {
      name: "Kailua-Kona DMV — Hawaii County",
      address: "74-5044 Ane Keohokalole Hwy, Kailua-Kona, HI 96740",
      hours: "Mon–Fri, 7:45 AM – 4:00 PM",
      note: "West Hawaii option. Appointment recommended.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "ITO",
      name: "Hilo International Airport",
      distance: "Primary airport — East Hawaii",
      address: "2450 Kekuanaoa St, Hilo, HI 96720",
      note: "Regional airport serving inter-island flights to Honolulu, Maui, and Kauai. International connections through HNL.",
      flightUrl: "https://hawaii.gov/ito",
      flightLabel: "ITO Airport Website",
      featured: true,
    },
    {
      code: "HNL",
      name: "Daniel K. Inouye International Airport (Honolulu)",
      distance: "International hub — Oahu, inter-island flight required",
      address: "300 Rodgers Blvd, Honolulu, HI 96819",
      note: "Major international hub for Hawaii. Hilo travelers connect through HNL for international flights.",
      flightUrl: "https://www.hawaii.gov/hnl/",
      flightLabel: "HNL Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel. We prioritize your application and submit directly via reserved agency slots.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Hilo and Big Island travelers renew fast and avoid delays — no agency visit required for most renewals.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time applicants typically require an in-person acceptance clerk visit. We guide you through every step of the process.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Replacements often require additional forms. We guide you through exactly what's needed to avoid costly mistakes and delays.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors (15 and under) require parental participation and an in-person acceptance step. We provide clear instructions and checklists.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or a legal name change — we assist with corrected passports and data corrections.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-book-2-line",
      title: "Second Passport & Damaged",
      description: "Need a second passport for frequent travel, or replacing a significantly damaged one? We handle both efficiently.",
      bullets: ["Frequent traveler solutions", "Damaged passport replacement", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Hilo and Big Island travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for Hilo residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance for Hilo residents with step-by-step guidance." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for Hilo residents with lost or stolen passports." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Hilo, with parental consent guidance." },
    { name: "Name Change", description: "Passport correction and name change service for Hilo residents after marriage or legal name changes." },
    { name: "Second Passport & Damaged", description: "Second passport issuance for frequent Hilo travelers and damaged passport replacement." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Government processing times change constantly. Many Hilo travelers assume 6–8 weeks is safe, only to find out it's now 3–4 months. Apply as soon as you book travel." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs so you get it right the first time." },
    { icon: "ri-file-damage-line", title: "Incomplete Forms", description: "One missing signature or wrong date and your application gets sent back. Our checklist catches errors before submission, saving you weeks of delay." },
    { icon: "ri-pen-nib-line", title: "Wrong Proof of Citizenship", description: "Not all birth certificates are accepted. Some require a long-form version. We tell you exactly which documents you need based on your situation." },
    { icon: "ri-calendar-close-line", title: "Missing Travel Proof for Emergency", description: "Regional agencies require proof of urgent travel. Without it, you're turned away. We help you prepare the right documentation before you need it." },
    { icon: "ri-map-pin-time-line", title: "Not Checking Name Consistency", description: "Your passport name must match your driver's license and ticket exactly. Middle names, suffixes, and hyphenated names cause more rejections than you'd think." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order. Takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist, instructions, and document review guidance." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using an overnight shipping option. Most Hilo clients ship from home — no office visit needed." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency availability — so you don't have to travel to Oahu or wait at an agency." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport is completed within the timeframe you selected. No guessing, no hoping an appointment opens up." },
  ],
  processingOptions: [
    { speed: "2–9", unit: "Business Days", label: "Fast Passport Center", tag: "Most Popular", tagColor: "bg-[#F6C344] text-[#1A365D]", features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"], highlight: true },
    { speed: "3–5", unit: "Weeks", label: "Fast Passport Center", tag: "Standard Expedited", tagColor: "bg-[#1A365D]/10 text-[#1A365D]", features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"], highlight: false, smartSaver: true },
    { speed: "1–2", unit: "Months", label: "Self-Service (Post Office)", tag: "Slowest", tagColor: "bg-gray-100 text-gray-400", features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"], highlight: false, muted: true },
    { speed: "24", unit: "Hours*", label: "Regional Agency Processing", tag: "Emergency Only", tagColor: "bg-gray-100 text-gray-500", features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"], highlight: false, emergency: true },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited availability, often weeks out", fpc: "No appointment needed to start", fpcGood: true },
    { feature: "In-Person Visit", agency: "Required — must travel to Honolulu on Oahu", fpc: "Most travelers ship documents overnight", fpcGood: true },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options", fpcGood: true },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review to reduce delays", fpcGood: true },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission availability", fpcGood: true },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected", fpcGood: true },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only", fpc: "Most U.S. citizens with required documents", fpcGood: true },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Honolulu", route: "/cities/honolulu" },
    { name: "Kailua-Kona", route: "/cities/kailua-kona" },
    { name: "Pahoa", route: "/pahoa" },
    { name: "Keaau", route: "/keaau" },
    { name: "Kahului", route: "/cities/kahului" },
    { name: "Kauai", route: "/cities/lihue" },
    { name: "Waimea", route: "/waimea-hi" },
    { name: "Kihei", route: "/cities/kihei" },
  ],
  fpcOfficeAddress: "300 Ala Moana Blvd, Room 1-224, Honolulu, HI 96850",
  fpcOfficeNote: "After signing up online, choose what works best for you: courier pickup from your home or office in most cases, ship with our prepaid label from any UPS/FedEx location on the Big Island, or schedule a drop-off. Most clients ship overnight — no visit required.",
  fpcOfficeDistance: "Serving Hilo and all of Big Island remotely",
  serviceAreaIntro: "Whether you are in Hilo, Pahoa, Keaau, or anywhere on the Big Island, Fast Passport Center makes expedited passport processing simple — no need to fly to Oahu, no agency lines.",
  nearbyCitiesIntro: "Anywhere on the Big Island — start online, ship overnight, and we handle the rest. No inter-island travel required.",
  comparisonIntro: "",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Hilo as simple as possible.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Hilo and Big Island travelers. Fast Passport Center reviews your documents before submission to catch these issues.",
  testimonialsIntro: "Real reviews from real travelers across Hilo, Pahoa, and Kailua-Kona who trusted Fast Passport Center with their expedited passports.",
  faqIntro: "Everything Hilo and Big Island travelers ask us most often about expedited passport services.",
  resourcesIntro: "",
  localTravelThemes: [],
  landmarks: [],
  island: "Big Island",
  islandNickname: "Hawaii Island",
  landmarkCards: [
    {
      name: "Hawaii Volcanoes National Park",
      description: "The crown jewel of the Big Island, this UNESCO World Heritage site draws international visitors from around the globe to witness active volcanic landscapes. Confirm your passport before booking international travel through Hilo.",
      imageUrl: "https://readdy.ai/api/search-image?query=Hawaii%20Volcanoes%20National%20Park%20Big%20Island%20at%20night%2C%20glowing%20lava%20flowing%20into%20ocean%2C%20dramatic%20volcanic%20landscape%2C%20steam%20vents%20and%20craters%2C%20tropical%20vegetation%20nearby%2C%20professional%20travel%20photography%2C%20vivid%20orange%20glow%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=hilo-volcanoes&orientation=landscape",
    },
    {
      name: "Rainbow Falls",
      description: "A stunning 80-foot waterfall just minutes from downtown Hilo, where morning light creates natural rainbows. Hilo is known as the wettest city in the U.S. — its lush landscape is world-class.",
      imageUrl: "https://readdy.ai/api/search-image?query=Rainbow%20Falls%20Hilo%20Hawaii%2C%20tropical%20waterfall%20cascading%20into%20pool%2C%20lush%20green%20rainforest%20with%20giant%20tropical%20leaves%2C%20morning%20mist%20with%20soft%20golden%20light%2C%20professional%20travel%20photography%2C%20vibrant%20green%20colors%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=hilo-rainbow-falls&orientation=landscape",
    },
    {
      name: "Mauna Kea Observatories",
      description: "The world's premier astronomical site sits at 13,796 feet. Hilo is the gateway to Mauna Kea, attracting researchers and stargazers from every corner of the globe — all with passports in hand.",
      imageUrl: "https://readdy.ai/api/search-image?query=Mauna%20Kea%20observatory%20summit%20Hawaii%20at%20sunset%2C%20white%20dome%20observatories%20on%20volcanic%20peak%20above%20clouds%2C%20dramatic%20orange%20sky%2C%20Big%20Island%20landscape%20below%2C%20professional%20travel%20photography%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=hilo-maunakea&orientation=landscape",
    },
  ],
  travelTips: [
    { icon: "ri-flight-takeoff-line", title: "Two Airports on the Island", description: "Hilo International (ITO) handles inter-island flights. Most international routes connect through Honolulu (HNL). Plan extra travel time and confirm your passport validity well in advance." },
    { icon: "ri-map-pin-line", title: "No Passport Agency on the Big Island", description: "The closest regional passport agency is in Honolulu — an inter-island flight away. Fast Passport Center eliminates the need for that trip entirely." },
    { icon: "ri-leaf-line", title: "Research & Science Travel", description: "Mauna Kea and USGS Volcano Observatory draw international researchers to Hilo. If you are in a research role with international travel needs, expedite early — conference seasons are busy." },
    { icon: "ri-truck-line", title: "Ship from Hilo", description: "FedEx and UPS locations in downtown Hilo offer overnight shipping. Ship documents before 6pm and they arrive at our processing center next morning." },
    { icon: "ri-global-line", title: "Japan & Pacific Connections", description: "Hilo has strong Japanese cultural ties. Many residents travel for Obon family visits and business. Golden Week and summer are peak passport demand times." },
    { icon: "ri-calendar-event-line", title: "Plan for Rain Delays", description: "Hilo is the wettest U.S. city. Weather can occasionally disrupt inter-island flights. Ship documents early to account for any potential shipping delays." },
  ],
  statePageRoute: "/states/hawaii",
  statePageName: "Hawaii",
  dmvWebsite: "https://www.hawaii.gov/dot/highways/mvm",
  dmvLabel: "Hawaii DMV — Official Website",
};